/* iter55h — Shared hook: poll the author's unread ops-reply count.
 *
 * Returns `{ unread, refresh }`. Public callers disable the hook until
 * the Loom AuthProvider has an authenticated user, avoiding anonymous
 * requests to this protected endpoint.
 *
 * Polls every 60 s while the tab is visible; skips polling when hidden
 * to avoid burning CPU on backgrounded landing pages.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { businessApi } from '@/lib/api';

export default function useBusinessUnread({ enabled = true, intervalMs = 60_000 } = {}) {
  const [unread, setUnread] = useState(0);
  const mountedRef = useRef(true);

  const fetchOnce = useCallback(async () => {
    try {
      const r = await businessApi.unreadCount();
      if (mountedRef.current) setUnread(Number(r?.unread || 0));
    } catch { /* 401 / offline → treat as 0 */ }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) return undefined;
    fetchOnce();
    let timer = null;
    const start = () => {
      if (timer) return;
      timer = window.setInterval(() => {
        if (!document.hidden) fetchOnce();
      }, intervalMs);
    };
    const stop = () => {
      if (timer) { window.clearInterval(timer); timer = null; }
    };
    start();
    const onVis = () => (document.hidden ? stop() : (fetchOnce(), start()));
    document.addEventListener('visibilitychange', onVis);
    return () => {
      mountedRef.current = false;
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [enabled, intervalMs, fetchOnce]);

  return { unread, refresh: fetchOnce };
}
